import { generateClient } from '@aws-amplify/api';
import { toDayRangeIso } from '../helpers/dateRange';
import { useDispatch, useSelector } from 'react-redux';
import {
  cashlessTransactionByEventIdCustom,
  cashlessTransactionByEventIdList,
  cashlessTransactionsByDeletedCustom,
} from '../constants/customQueries';
import { cashlessTransactionByGuestID } from '../graphql/queries';
import {
  CashlessTransactionListingVariables,
  CashlessTransactionStatisticsByEventParams,
  CashlessTransactionStatisticsByEventResult,
  HeadCell,
} from '../models/app';
import {
  setFilter,
  setListing,
  setNextToken,
} from '../store/ducks/cashlessTransactions';
import { CashlessTransaction, ModelSortDirection } from './../models/GQL_API';
import useApp from './useApp';

const client = generateClient();

const SEARCH_FIELDS = [
  'pos',
  'checkNumber',
  'guestPhoneNumber',
  'guestName',
  'guestEmail',
  'type',
];

/**
 * `contains` is case-sensitive in DynamoDB, and these fields are stored in
 * mixed case, so match both the typed text and its lowercase form.
 */
const buildSearchConditions = (searchText: string) => {
  const values = Array.from(new Set([searchText, searchText.toLowerCase()]));
  return SEARCH_FIELDS.flatMap((field) =>
    values.map((value) => ({ [field]: { contains: value } })),
  );
};

/**
 * One page of cashless transactions. With an event, read that event's
 * partition of the cashlessTransactionByEventID GSI (createdAt key
 * condition, `deleted` as a filter) instead of every non-deleted transaction
 * via the `deleted` GSI. `variables` are the deleted-index variables.
 */
const queryTransactionsPage = async (
  variables: any,
  eventId?: string | null,
): Promise<{ items: any[]; nextToken: string | null | undefined }> => {
  if (eventId) {
    const { deleted, ...rest } = variables;
    const response: any = await client.graphql({
      query: cashlessTransactionByEventIdList,
      variables: {
        ...rest,
        eventId,
        filter: { ...(rest.filter ?? {}), deleted: { eq: deleted ?? '0' } },
      },
      authMode: 'userPool',
    });
    return {
      items: response.data.cashlessTransactionByEventID.items ?? [],
      nextToken: response.data.cashlessTransactionByEventID.nextToken,
    };
  }
  const response: any = await client.graphql({
    query: cashlessTransactionsByDeletedCustom,
    variables,
    authMode: 'userPool',
  });
  return {
    items: response.data.cashlessTransactionsByDeleted.items ?? [],
    nextToken: response.data.cashlessTransactionsByDeleted.nextToken,
  };
};

const useCashlessTransaction = (listingName: string, singleName: string) => {
  const dispatch = useDispatch();
  const { showError } = useApp();
  const cashlessTransactionsFilter = useSelector(
    (state: any) => state.cashlessTransactions.filter,
  );

  const nextToken = useSelector(
    (state: any) => state[`${listingName}`]['nextToken'],
  );

  async function fetch(params: CashlessTransactionListingVariables) {
    try {
      const {
        searchText,
        limit,
        generalFilters,
        guestId,
        eventFilter,
        adminIdFilter,
        bookedTicketTypeFilter,
      } = params;

      const filter: any = {
        // deleted: { eq: '0' },
      };
      const createdAtFilter: any = {};
      filter.or = [];
      filter.and = [];
      if (searchText.length > 0) {
        filter.or.push(...buildSearchConditions(searchText));
      }

      if (eventFilter) {
        filter.and.push({ eventId: { eq: eventFilter } });
      }

      if (adminIdFilter) {
        filter.and.push({ createdByID: { eq: adminIdFilter } });
      }

      if (bookedTicketTypeFilter) {
        filter.and.push({ type: { eq: bookedTicketTypeFilter } });
      }

      // Whole local days [from 00:00, to 23:59:59.999].
      const createdAtRange = toDayRangeIso(
        generalFilters?.fromDate,
        generalFilters?.toDate,
      );
      if (createdAtRange) {
        createdAtFilter.between = createdAtRange;
      }

      if (filter.and && filter.and.length === 0) {
        delete filter.and;
      }
      if (filter.or && filter.or.length === 0) {
        delete filter.or;
      }

      const variables: any = {
        filter,
        limit: 1000,
        nextToken: nextToken,
        sortDirection: 'DESC',
        guestId,
      };

      if (createdAtFilter.between) {
        variables.createdAt = createdAtFilter;
      }

      const groupsList: any = await client.graphql<CashlessTransaction>({
        query: cashlessTransactionByGuestID,
        variables: variables,
        authMode: 'userPool',
      });
      const currentNextToken =
        groupsList.data.cashlessTransactionByGuestID.nextToken;
      const listing = groupsList.data.cashlessTransactionByGuestID.items;
      dispatch(setListing(listing));
      dispatch(setNextToken(currentNextToken));
      return listing;
    } catch (err: Error | any) {
      showError(err.message || err);
      return [];
    }
  }

  async function fetchSortedByDate(
    params: CashlessTransactionListingVariables,
  ) {
    try {
      const {
        searchText,
        limit,
        nextToken,
        generalFilters,
        eventFilter,
        adminIdFilter,
        bookedTicketTypeFilter,
      } = params;

      const filter: any = {};
      const createdAtFilter: any = {};
      filter.or = [];
      filter.and = [];
      let requestNextToken = nextToken;

      if (searchText.length > 0) {
        filter.or.push(...buildSearchConditions(searchText));
      }

      // eventFilter: key condition of the event index (see queryTransactionsPage).

      if (adminIdFilter) {
        filter.and.push({ createdByID: { eq: adminIdFilter } });
      }

      if (bookedTicketTypeFilter) {
        filter.and.push({ type: { eq: bookedTicketTypeFilter } });
      }

      // Whole local days [from 00:00, to 23:59:59.999].
      const createdAtRange = toDayRangeIso(
        generalFilters?.fromDate,
        generalFilters?.toDate,
      );
      if (createdAtRange) {
        createdAtFilter.between = createdAtRange;
      }

      if (filter.and && filter.and.length === 0) {
        delete filter.and;
      }
      if (filter.or && filter.or.length === 0) {
        delete filter.or;
      }

      if (
        cashlessTransactionsFilter &&
        cashlessTransactionsFilter.toString() !==
        [
          JSON.stringify(generalFilters),
          eventFilter,
          adminIdFilter,
          bookedTicketTypeFilter,
          searchText,
          limit,
          searchText,
        ].toString()
      ) {
        requestNextToken = null;
      }

      const variables: any = {
        filter,
        limit: 1000,
        sortDirection: 'DESC',
        deleted: '0',
      };

      if (requestNextToken) {
        variables.nextToken = requestNextToken;
      }

      if (createdAtFilter.between) {
        variables.createdAt = createdAtFilter;
      }

      const page = await queryTransactionsPage(variables, eventFilter);
      let currentNextToken = page.nextToken;
      let listing = page.items;

      dispatch(
        setFilter([
          JSON.stringify(generalFilters),
          eventFilter,
          adminIdFilter,
          bookedTicketTypeFilter,
          searchText,
          limit,
          searchText,
        ]),
      );

      return { listing, nextToken: currentNextToken };
    } catch (err: Error | any) {
      showError(err.message || err);
      return [];
    }
  }

  async function fetchAllSortedByDate(
    params: CashlessTransactionListingVariables,
  ) {
    try {
      const {
        searchText,
        generalFilters,
        eventFilter,
        adminIdFilter,
        bookedTicketTypeFilter,
      } = params;

      const filter: any = {};
      const createdAtFilter: any = {};
      filter.or = [];
      filter.and = [];

      if (searchText.length > 0) {
        filter.or.push(...buildSearchConditions(searchText));
      }

      // eventFilter: key condition of the event index (see queryTransactionsPage).

      if (adminIdFilter) {
        filter.and.push({ createdByID: { eq: adminIdFilter } });
      }

      if (bookedTicketTypeFilter) {
        filter.and.push({ type: { eq: bookedTicketTypeFilter } });
      }

      // Whole local days [from 00:00, to 23:59:59.999].
      const createdAtRange = toDayRangeIso(
        generalFilters?.fromDate,
        generalFilters?.toDate,
      );
      if (createdAtRange) {
        createdAtFilter.between = createdAtRange;
      }

      if (filter.and && filter.and.length === 0) {
        delete filter.and;
      }
      if (filter.or && filter.or.length === 0) {
        delete filter.or;
      }

      const variables: any = {
        filter,
        limit: 1000,
        sortDirection: 'DESC',
        deleted: '0',
      };

      if (createdAtFilter.between) {
        variables.createdAt = createdAtFilter;
      }

      const firstPage = await queryTransactionsPage(variables, eventFilter);
      let currentNextToken = firstPage.nextToken;
      let listing = firstPage.items;
      while (currentNextToken) {
        // (the token was never passed before, so a 2nd page looped forever)
        variables.nextToken = currentNextToken;
        const page = await queryTransactionsPage(variables, eventFilter);
        currentNextToken = page.nextToken;
        listing = listing.concat(page.items);
      }
      return listing;
    } catch (err: Error | any) {
      showError(err.message || err);
      return [];
    }
  }

  async function fetchTransactionStatisticsByEvent(
    params: CashlessTransactionStatisticsByEventParams,
  ) {
    const statisticsResult: CashlessTransactionStatisticsByEventResult = {
      totalRevenue: 0,
    };
    try {
      // fetch all topups by event id
      const allEventTransactions: CashlessTransaction[] =
        await fetchAllTransactionByEventId(params);

      for (const transaction of allEventTransactions) {
        statisticsResult.totalRevenue += transaction.paidAmount;
      }

      return statisticsResult;
    } catch (err) {
      return statisticsResult;
    }
  }

  async function fetchAllTransactionByEventId(
    params: CashlessTransactionStatisticsByEventParams,
  ) {
    try {
      const { eventId, adminId } = params;
      if (!eventId) return [];
      const filter: any = { deleted: { eq: '0' } };
      if (adminId) {
        filter.createdByID = { eq: adminId };
      }
      let allListing: CashlessTransaction[] = [];
      let nextToken = undefined;

      do {
        const variables = {
          filter,
          sortDirection: ModelSortDirection.DESC,
          limit: 1000,
          nextToken,
          eventId,
        };
        const response: any = await client.graphql<CashlessTransaction>({
          query: cashlessTransactionByEventIdCustom,
          variables: variables,
          authMode: 'userPool',
        });
        allListing.push(...response.data.cashlessTransactionByEventID.items);
        nextToken = response.data.cashlessTransactionByEventID.nextToken;
      } while (nextToken);

      return allListing;
    } catch (err) {
      return [];
    }
  }

  const headCells: readonly HeadCell[] = [
    {
      id: 'guest',
      numeric: false,
      disablePadding: false,
      label: 'Guest Name',
    },
    {
      id: 'guest',
      numeric: false,
      disablePadding: false,
      label: 'Guest Phone',
    },
    {
      id: 'guest',
      numeric: false,
      disablePadding: false,
      label: 'Guest Email',
    },
    // {
    //   id: 'pos',
    //   numeric: false,
    //   disablePadding: false,
    //   label: 'POS',
    // },
    // {
    //   id: 'checkNumber',
    //   numeric: false,
    //   disablePadding: false,
    //   label: 'Check #',
    // },
    {
      id: 'type',
      numeric: false,
      disablePadding: false,
      label: 'Type',
    },
    {
      id: 'event',
      numeric: false,
      disablePadding: false,
      label: 'Event Name',
    },
    {
      id: 'orderTotal',
      numeric: false,
      disablePadding: false,
      label: 'Order Total',
    },
    {
      id: 'paidAmount',
      numeric: false,
      disablePadding: false,
      label: 'Paid Amount',
    },
    // {
    //   id: 'paymentStatus',
    //   numeric: false,
    //   disablePadding: false,
    //   label: 'Payment Status',
    // },
    {
      id: 'createdAt',
      numeric: true,
      disablePadding: false,
      label: 'Date',
    },
    {
      id: 'createdByName',
      numeric: true,
      disablePadding: false,
      label: 'Created By',
    },
  ];
  const dataCells: readonly string[] = [
    'POS',
    'checkNumber',
    'event',
    'orderTotal',
    'paidAmount',
    // 'paymentStatus',
    'createdAt',
    'createdByName',
  ];
  const api: any = {};

  api[`${listingName}HeadCells`] = headCells;
  api[`${listingName}DataCells`] = dataCells;
  api[`${listingName}Fetch`] = fetch;
  api[`${listingName}FetchSortedByDate`] = fetchSortedByDate;
  api[`${listingName}FetchAllSortedByDate`] = fetchAllSortedByDate;
  api[`${listingName}FetchStatisticsByEvent`] =
    fetchTransactionStatisticsByEvent;
  api[`${listingName}ChangeListing`] = (listing: CashlessTransaction[]) =>
    dispatch(setListing(listing));
  return api;
};
export default useCashlessTransaction;
